<template>
  <div class="p-6 bg-white rounded-lg shadow-md">
    <h1 class="text-3xl font-bold mb-6 text-gray-800">Gestion du Personnel Visité</h1>

    <div class="mb-6 p-4 border border-gray-200 rounded-lg bg-gray-50">
      <label for="visitedPeopleCompany" class="block text-sm font-medium text-gray-700 mb-2">Société</label>
      <select
        id="visitedPeopleCompany"
        v-model="selectedCompanyId"
        class="w-full md:w-96 border border-gray-300 rounded-md shadow-sm p-2 bg-white"
      >
        <option v-for="company in companies" :key="company.id" :value="company.id">
          {{ company.name }}
        </option>
      </select>
    </div>

    <div class="mb-4 flex gap-2">
      <input
        type="text"
        v-model="newVisitedPerson"
        placeholder="Nouveau personnel visité"
        class="flex-1 border border-gray-300 rounded-md shadow-sm p-2"
      />
      <button
        @click="addVisitedPerson"
        class="px-4 py-2 bg-orange-600 text-white font-semibold rounded-md shadow-sm hover:bg-orange-700"
      >
        Ajouter
      </button>
    </div>

    <ul class="border border-gray-200 rounded-md divide-y divide-gray-200 bg-white">
      <li v-for="person in visitedPeople" :key="person.id" class="px-4 py-3 flex justify-between items-center">
        <span>{{ person.full_name }}</span>
        <div class="space-x-3">
          <button @click="renameVisitedPerson(person)" class="text-blue-600 hover:text-blue-900">Renommer</button>
          <button @click="deleteVisitedPerson(person.id)" class="text-red-600 hover:text-red-900">Supprimer</button>
        </div>
      </li>
      <li v-if="visitedPeople.length === 0" class="px-4 py-3 text-gray-500">Aucun personnel.</li>
    </ul>

    <p v-if="errorMessage" class="text-red-600 text-sm mt-4">{{ errorMessage }}</p>
    <p v-if="successMessage" class="text-green-600 text-sm mt-4">{{ successMessage }}</p>
  </div>
</template>

<script>
import { ref, onMounted, watch } from 'vue';
import apiFetch, { companyApi } from '@/apiClient';

export default {
  name: 'VisitedPeopleManagementView',
  setup() {
    const companies = ref([]);
    const selectedCompanyId = ref(null);
    const visitedPeople = ref([]);
    const newVisitedPerson = ref('');
    const errorMessage = ref('');
    const successMessage = ref('');

    const selectedCompany = () => companies.value.find((company) => company.id === selectedCompanyId.value);

    const fetchCompanies = async () => {
      const data = await apiFetch('/companies');
      companies.value = data;

      const administration = companies.value.find((company) => company.name === 'Administration');
      selectedCompanyId.value = administration ? administration.id : (companies.value[0]?.id || null);
    };

    const fetchVisitedPeople = async () => {
      if (!selectedCompanyId.value) {
        visitedPeople.value = [];
        return;
      }
      visitedPeople.value = await companyApi.getVisitedPeople(selectedCompanyId.value);
    };

    const addVisitedPerson = async () => {
      const fullName = newVisitedPerson.value.trim();
      if (!selectedCompanyId.value || !fullName) return;

      await companyApi.createVisitedPerson(selectedCompanyId.value, { full_name: fullName });
      successMessage.value = `Personnel ajouté pour ${selectedCompany()?.name || 'la société'}.`;
      errorMessage.value = '';
      newVisitedPerson.value = '';
      await fetchVisitedPeople();
    };

    const renameVisitedPerson = async (person) => {
      const fullName = prompt('Nouveau nom du personnel', person.full_name);
      if (!fullName || !fullName.trim()) return;

      await companyApi.updateVisitedPerson(selectedCompanyId.value, person.id, { full_name: fullName.trim() });
      successMessage.value = 'Personnel mis à jour.';
      errorMessage.value = '';
      await fetchVisitedPeople();
    };

    const deleteVisitedPerson = async (personId) => {
      if (!confirm('Supprimer ce personnel ?')) return;

      await companyApi.deleteVisitedPerson(selectedCompanyId.value, personId);
      successMessage.value = 'Personnel supprimé.';
      errorMessage.value = '';
      await fetchVisitedPeople();
    };

    onMounted(async () => {
      errorMessage.value = '';
      successMessage.value = '';
      try {
        await fetchCompanies();
        await fetchVisitedPeople();
      } catch (error) {
        errorMessage.value = error.message;
      }
    });

    watch(selectedCompanyId, async () => {
      errorMessage.value = '';
      successMessage.value = '';
      try {
        await fetchVisitedPeople();
      } catch (error) {
        errorMessage.value = error.message;
      }
    });

    return {
      companies,
      selectedCompanyId,
      visitedPeople,
      newVisitedPerson,
      errorMessage,
      successMessage,
      addVisitedPerson,
      renameVisitedPerson,
      deleteVisitedPerson,
    };
  },
};
</script>
